import type { ReactElement, ReactNode } from 'react';

import type { DataGridProps } from './DataGrid';

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export type StateSetter<S> = React.Dispatch<React.SetStateAction<S>>;

export type CellState = {
  /** Cannot enter edit mode. Wins over renderEditCell presence. */
  locked: boolean;
  /** Required-but-empty styling. Only applied when also editable. */
  required: boolean;
  /** Triggers emptyCellClassname. */
  empty: boolean;
  /** Triggers invalidClassname. */
  valid: boolean;
};

export type CellProps<TRow, TSummaryRow = unknown> = {
  renderCell?: (props: RenderCellProps<TRow, TSummaryRow>) => ReactNode;
  renderEditCell?: (props: RenderEditCellProps<TRow, TSummaryRow>) => ReactNode;
  className?: string;
  locked?: boolean;
  /** Required-but-empty styling. Only applied when also editable. */
  required?: boolean;
  /** Triggers emptyCellClassname. */
  empty?: boolean;
  /** Triggers invalidClassname. */
  valid?: boolean;
  /**
   * Render the cell content in addition to the edit cell.
   * Enable this option when the editor is rendered outside the grid, like a modal for example.
   * By default, the cell content is not rendered when the edit cell is open.
   * !! If this is true won't commit cell on click outside
   * @default false
   */
  displayCellContent?: boolean;
  dataAttrs?: Record<`data-${string}`, string>;
};

export type CellPropsResolver<TRow, TSummaryRow = unknown> = (row: TRow) => CellProps<TRow, TSummaryRow>;

export type Cell<TRow, TSummaryRow = unknown> = {
  state: CellState;
  renderCell?: (props: RenderCellProps<TRow, TSummaryRow>) => ReactNode;
  renderEditCell?: (props: RenderEditCellProps<TRow, TSummaryRow>) => ReactNode;
  cellClass?: string;
  displayCellContent: boolean;
  dataAttrs?: Record<`data-${string}`, string>;
};

export interface Column<TRow, TSummaryRow = unknown> {
  /** The name of the column. By default it will be displayed in the header cell */
  readonly name: string | ReactElement;
  /** A unique key to distinguish each column */
  readonly key: string;
  /** Column width. If not specified, it will be determined automatically based on grid width and specified widths of other columns */
  readonly width?: number | string;
  readonly minWidth?: number;
  readonly maxWidth?: number;
  readonly headerCellClass?: string;
  readonly summaryCellClass?: string | ((row: TSummaryRow) => string | undefined);
  readonly renderHeaderCell?: (props: RenderHeaderCellProps<TRow, TSummaryRow>) => ReactNode;
  readonly renderSummaryCell?: (props: RenderSummaryCellProps<TSummaryRow, TRow>) => ReactNode;
  readonly colSpan?: (args: ColSpanArgs<TRow, TSummaryRow>) => number | undefined;
  /** Determines whether column is frozen or not */
  readonly frozen?: boolean;
  /** Enable resizing of a column */
  readonly resizable?: boolean;
  /** Enable sorting of a column */
  readonly sortable?: boolean;
  /** Enable dragging of a column */
  readonly draggable?: boolean;
  /** Sets the column sort order to be descending instead of ascending the first time the column is sorted */
  readonly sortDescendingFirst?: boolean;
  readonly cell: CellProps<TRow, TSummaryRow> | CellPropsResolver<TRow, TSummaryRow>;
}

export interface CalculatedColumn<TRow, TSummaryRow = unknown> extends Column<TRow, TSummaryRow> {
  readonly parent: CalculatedColumnParent<TRow, TSummaryRow> | undefined;
  readonly idx: number;
  readonly level: number;
  readonly width: number | string;
  readonly minWidth: number;
  readonly maxWidth: number | undefined;
  readonly resizable: boolean;
  readonly sortable: boolean;
  readonly draggable: boolean;
  readonly frozen: boolean;
}

export interface ColumnGroup<R, SR = unknown> {
  /** The name of the column group, it will be displayed in the header cell */
  readonly name: string | ReactElement;
  readonly headerCellClass?: string;
  readonly children: readonly ColumnOrColumnGroup<R, SR>[];
}

export interface CalculatedColumnParent<R, SR> {
  readonly name: string | ReactElement;
  readonly parent: CalculatedColumnParent<R, SR> | undefined;
  readonly idx: number;
  readonly colSpan: number;
  readonly level: number;
  readonly headerCellClass?: string;
}

export type ColumnOrColumnGroup<R, SR = unknown> = Column<R, SR> | ColumnGroup<R, SR>;

export type CalculatedColumnOrColumnGroup<R, SR> = CalculatedColumnParent<R, SR> | CalculatedColumn<R, SR>;

export interface Position {
  readonly idx: number;
  readonly rowIdx: number;
}

/** Normalized rectangular cell range: start is the top-left corner, end the bottom-right (both inclusive). */
export interface CellRangeSelection {
  readonly startIdx: number;
  readonly startRowIdx: number;
  readonly endIdx: number;
  readonly endRowIdx: number;
}

/**
 * A single row's view of the active range: the rectangle's column slice (identical for
 * every row) plus this row's vertical-edge flags. `null` when the row lies outside the
 * range (or there is no range). Keeping the constant column span here once, instead of
 * recomputing it per cell, lets each row stay self-contained without knowing the whole
 * rectangle's row coordinates.
 */
export interface CellRangeRowSlice {
  readonly startIdx: number;
  readonly endIdx: number;
  readonly isTopRow: boolean;
  readonly isBottomRow: boolean;
}

/**
 * A copied rectangle addressed by data keys, not viewport indices, so it
 * survives sort/filter changes between copy and paste. The grid resolves it
 * against its current row/column order (see the `copiedOutline` prop) and
 * draws the marching-ants outline only while the keys still form a contiguous
 * rectangle.
 */
export interface CopiedOutline {
  /** Ties the outline to the clipboard payload written by the same copy. */
  readonly copyId: string;
  /** Row keys of the copied rectangle, as produced by `rowKeyGetter`. */
  readonly rowKeys: readonly string[];
  /** Leaf column keys of the copied rectangle, in visual order. */
  readonly columnKeys: readonly string[];
}

export interface PresenceMarker {
  /** Row key, as produced by `rowKeyGetter`. */
  rowKey: string;
  /** Column key. */
  columnKey: string;
  /** Outline color drawn around the cell. */
  color: string;
  /** Optional hover label (e.g. user name); multiple on a cell are joined with `, `. */
  title?: string;
}

/** Markers for a single row, grouped by column key. */
export type RowPresence = ReadonlyMap<string, readonly PresenceMarker[]>;

export const gridTooltipId = 'rdg-tooltip';

export interface RenderCellProps<TRow, TSummaryRow = unknown> {
  column: CalculatedColumn<TRow, TSummaryRow>;
  row: TRow;
  rowIdx: number;
  isCellEditable: boolean;
  cellState: CellState;
  tabIndex: number;
  onRowChange: (row: TRow) => void;
}

export interface RenderSummaryCellProps<TSummaryRow, TRow = unknown> {
  column: CalculatedColumn<TRow, TSummaryRow>;
  row: TSummaryRow;
  tabIndex: number;
}

export interface RenderEditCellProps<TRow, TSummaryRow = unknown> {
  column: CalculatedColumn<TRow, TSummaryRow>;
  row: TRow;
  cellState: CellState;
  onRowChange: (row: TRow, commitChanges?: boolean, shouldKeepOpen?: boolean) => void;
  onClose: (commitChanges?: boolean, shouldFocusCell?: boolean) => void;
}

export interface RenderHeaderCellProps<TRow, TSummaryRow = unknown> {
  column: CalculatedColumn<TRow, TSummaryRow>;
  sortDirection: SortDirection | undefined;
  priority: number | undefined;
  tabIndex: number;
}

export interface CellRendererProps<TRow, TSummaryRow>
  extends Pick<RenderRowProps<TRow, TSummaryRow>, 'row' | 'rowIdx' | 'selectCell'>,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'style' | 'children' | 'onClick' | 'onDoubleClick' | 'onContextMenu'> {
  column: CalculatedColumn<TRow, TSummaryRow>;
  colSpan: number | undefined;
  isDraggedOver: boolean;
  isCellSelected: boolean;
  rangeClassname: string | undefined;
  copiedClassname: string | undefined;
  presence?: readonly PresenceMarker[];
  onClick: RenderRowProps<TRow, TSummaryRow>['onCellClick'];
  onDoubleClick: RenderRowProps<TRow, TSummaryRow>['onCellDoubleClick'];
  onContextMenu: RenderRowProps<TRow, TSummaryRow>['onCellContextMenu'];
  onCellMouseDown: ((position: Position, event: React.MouseEvent<HTMLDivElement>) => void) | undefined;
  onCellMouseEnter: ((position: Position) => void) | undefined;
  onRowChange: (column: CalculatedColumn<TRow, TSummaryRow>, newRow: TRow) => void;
}

export type CellEvent<E extends React.SyntheticEvent<HTMLDivElement>> = E & {
  preventGridDefault: () => void;
  isGridDefaultPrevented: () => boolean;
};

export type CellMouseEvent = CellEvent<React.MouseEvent<HTMLDivElement>>;

export type CellKeyboardEvent = CellEvent<React.KeyboardEvent<HTMLDivElement>>;

export interface CellClickArgs<TRow, TSummaryRow = unknown> {
  row: TRow;
  column: CalculatedColumn<TRow, TSummaryRow>;
  selectCell: (enableEditor?: boolean) => void;
}

interface SelectCellKeyDownArgs<TRow, TSummaryRow = unknown> {
  mode: 'SELECT';
  row: TRow;
  column: CalculatedColumn<TRow, TSummaryRow>;
  rowIdx: number;
  selectCell: (position: Position, enableEditor?: boolean) => void;
}

export interface EditCellKeyDownArgs<TRow, TSummaryRow = unknown> {
  mode: 'EDIT';
  row: TRow;
  column: CalculatedColumn<TRow, TSummaryRow>;
  rowIdx: number;
  navigate: () => void;
  onClose: (commitChanges?: boolean, shouldFocusCell?: boolean) => void;
}

export type CellKeyDownArgs<TRow, TSummaryRow = unknown> =
  | SelectCellKeyDownArgs<TRow, TSummaryRow>
  | EditCellKeyDownArgs<TRow, TSummaryRow>;

export interface CellSelectArgs<TRow, TSummaryRow = unknown> {
  rowIdx: number;
  row: TRow;
  column: CalculatedColumn<TRow, TSummaryRow>;
}

export interface BaseRenderRowProps<TRow, TSummaryRow = unknown>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'style' | 'children'>,
    Pick<DataGridProps<TRow, TSummaryRow>, 'onCellClick' | 'onCellDoubleClick' | 'onCellContextMenu'> {
  viewportColumns: readonly CalculatedColumn<TRow, TSummaryRow>[];
  rowIdx: number;
  selectedCellIdx: number | undefined;
  isRowSelected: boolean;
  gridRowStart: number;
  height: number;
  selectCell: (position: Position, enableEditor?: boolean) => void;
}

export interface RenderRowProps<TRow, TSummaryRow = unknown> extends BaseRenderRowProps<TRow, TSummaryRow> {
  row: TRow;
  lastFrozenColumnIndex: number;
  copiedSlice: CellRangeRowSlice | null;
  draggedOverCellIdx: number | undefined;
  rangeSlice: CellRangeRowSlice | null;
  onCellMouseDown: ((position: Position, event: React.MouseEvent<HTMLDivElement>) => void) | undefined;
  onCellMouseEnter: ((position: Position) => void) | undefined;
  rowPresence: RowPresence | undefined;
  selectedCellEditor: ReactElement<RenderEditCellProps<TRow>> | undefined;
  onRowChange: (column: CalculatedColumn<TRow, TSummaryRow>, rowIdx: number, newRow: TRow) => void;
  rowClass?: (row: TRow, rowIdx: number) => string | undefined;
  setDraggedOverRowIdx: ((overRowIdx: number) => void) | undefined;
  'data-testid'?: string;
}

export interface RowsChangeData<R, SR = unknown> {
  indexes: number[];
  column: CalculatedColumn<R, SR>;
}

export type SelectRowEvent<TRow> =
  | { type: 'HEADER'; checked: boolean }
  | { type: 'ROW'; row: TRow; checked: boolean; isShiftClick: boolean };

export interface FillEvent<TRow> {
  columnKey: string;
  sourceRow: TRow;
  targetRow: TRow;
}

export interface SortColumn {
  readonly columnKey: string;
  readonly direction: SortDirection;
}

export type CellNavigationMode = 'NONE' | 'CHANGE_ROW';
export type SortDirection = 'ASC' | 'DESC';

export type ColSpanArgs<TRow, TSummaryRow> =
  | { type: 'HEADER' }
  | { type: 'ROW'; row: TRow }
  | { type: 'SUMMARY'; row: TSummaryRow };

export type RowHeightArgs<TRow> = { type: 'ROW'; row: TRow };

export interface RenderSortIconProps {
  sortDirection: SortDirection | undefined;
}

export interface RenderSortPriorityProps {
  priority: number | undefined;
}

export interface RenderSortStatusProps extends RenderSortIconProps, RenderSortPriorityProps {}

export interface RenderCheckboxProps
  extends Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    'aria-label' | 'aria-labelledby' | 'checked' | 'tabIndex' | 'disabled'
  > {
  onChange: (checked: boolean, shift: boolean) => void;
}
